// The arithmetic of answering a queue of patterns (B47), kept out of the component so it
// can be proved. Two counts are easy to confuse and both are on the screen:
//
//   patternCount        what the list says a question stands for: the caller's open flags
//                       of that pattern, this one included.
//   settledByPattern    what the answer actually settled: the OTHERS the cascade closed.
//                       migration 29 excludes the answered flag itself (f.id <> NEW.id,
//                       and a constraint forbids answered_via_flag_id = id), so one answer
//                       disposes of settledByPattern + 1 questions, never settledByPattern.
//
// The cascade can settle fewer than the pattern promised — it skips another flag on the
// same signal, and a tier the other flag does not offer — so the total here is always
// what the API reported, never what the list predicted.
import { count } from './labels.js';

export interface QueueState {
  /** Position in the list of patterns. */
  at: number;
  /** The last thing to say to the user; null when there is nothing to say. */
  said: string | null;
  /** Answers given this sitting. */
  answered: number;
  /** Questions disposed of this sitting, the answered flags included. */
  settled: number;
}

export const start: QueueState = { at: 0, said: null, answered: 0, settled: 0 };

export type QueueEvent =
  /** Move on without answering. Nothing is written: a skip is not a judgement. */
  | { kind: 'skip' }
  /** The API took the answer. `settledByPattern` is its figure for the others. */
  | { kind: 'answered'; settledByPattern: number }
  /** 409: the flag was settled meanwhile, by another sitting or by a pattern. Not a failure. */
  | { kind: 'gone' }
  /** Anything else the API said, in its own words. The queue does not move. */
  | { kind: 'failed'; message: string };

export function reduce(s: QueueState, e: QueueEvent): QueueState {
  switch (e.kind) {
    case 'skip':
      return { ...s, at: s.at + 1, said: null };
    case 'answered': {
      const others = Math.max(e.settledByPattern, 0);
      return {
        at: s.at + 1,
        answered: s.answered + 1,
        settled: s.settled + others + 1,
        said: others > 0 ? `Answered, and ${count(others, 'question')} of the same shape with it.` : 'Answered.',
      };
    }
    case 'gone':
      return { ...s, at: s.at + 1, said: 'That one was already settled. Moving on.' };
    case 'failed':
      return { ...s, said: e.message };
  }
}

/** "Question 3 of 788", grouped. */
export function positionLine(at: number, patterns: number): string {
  return `Question ${(at + 1).toLocaleString('en-GB')} of ${patterns.toLocaleString('en-GB')}`;
}

/**
 * The open flags still behind the queue: what the list carried when it loaded, less what
 * this sitting has settled. Never below zero, and never a guess — both numbers are the
 * API's.
 */
export function remaining(openAtLoad: number, settled: number): number {
  return Math.max(openAtLoad - settled, 0);
}

/** What the sitting came to, for the end of the queue. */
export function sittingLine(s: QueueState): string {
  if (s.answered === 0) return 'Nothing answered this time.';
  return `${count(s.answered, 'answer')} settled ${count(s.settled, 'question')} in all.`;
}

/**
 * Whether the prompt should come up on its own (B57). Cobrain brings the question to the
 * user, but it asks once per page load: if they closed it, it stays closed until they ask
 * for it, because a prompt that reopens itself is a prompt people learn to dismiss without
 * reading. Nothing here writes; a closed prompt is not an answer.
 */
export function shouldOpen(o: { ready: boolean; patternsLeft: number; alreadyOffered: boolean }): boolean {
  return o.ready && !o.alreadyOffered && o.patternsLeft > 0;
}
